import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContext, Table, TableBody, TableCell, TableHeader, TableRow } from 'grommet';
import { Box, BoxProps, Text } from '/src/components';
import {
  IncentiveStatementEquipmentCategory,
  IncentiveStatementFacility,
  IncentiveStatementFinancials,
  IncentiveStatementProgram,
  IncentiveStatementResponse,
  TCountryId,
  normalizeIncentiveStatementResponse,
} from '/src/lib/models';
import { CountryId } from '/src/lib/types';
import { formatNumber, getIsMobile, pxToPt } from '/src/utils';

export const IncentiveStatementDataTable: React.FC<IncentiveStatementDataTableProps> = (props) => {
  /* PROPS */
  const { incentiveStatement: response, isSummary, countryId, ...boxProps } = props;

  /* CONTEXT */
  const screenSize = useContext(ResponsiveContext);
  const isMobile = getIsMobile(screenSize);
  const { t } = useTranslation();

  const tableHeaders = useMemo(
    () => [
      t('common:program'),
      t('common:totalKwh'),
      t('statements:credits'),
      t('statements:creditPrice'),
      t('statements:totalValue'),
      t('statements:recCost'),
      t('statements:brokerCost'),
      t('statements:fuseServiceFee'),
      t('statements:netIncentive'),
    ],
    [t]
  );

  const canadianHeaders = useMemo(
    () => [
      t('common:program'),
      t('common:totalKwh'),
      t('statements:credits'),
      t('statements:creditPrice'),
      t('statements:totalValue'),
      t('statements:taxesOnCreditSale'),
      t('statements:taxesOnServices'),
      t('statements:fuseServiceFee'),
      t('statements:netIncentive'),
    ],
    [t]
  );

  // Cells
  const getBackground = (rowIndex: number) => (rowIndex % 2 ? 'white' : 'light-6');
  const verticalPad = pxToPt('4px');
  const horizontalPad = pxToPt('4px');
  const cellBorder: BoxProps['border'] = { size: '0' };
  const totalsCellBorder: BoxProps['border'] = { side: 'top', size: 'xsmall', color: 'text' };
  const horizontalMargin = pxToPt('18px');
  const facilityIndent = pxToPt('12px');
  const categoryIndent = pxToPt('24px');
  const nameCellSizePct = 25;
  const currencyCellSizePct = 9;

  // Text
  const lineHeight = pxToPt('18px');
  const headerSize = pxToPt('11px');
  const programValueSize = pxToPt('10px');
  const facilityValueSize = pxToPt('10px');
  const categoryValueSize = pxToPt('10px');
  const totalsSize = pxToPt('11px');
  const grandTotalSize = pxToPt('13px');
  const maxHeaderWidth = pxToPt('102px');

  /* Memos */
  const normalized = useMemo(() => normalizeIncentiveStatementResponse(response), [response]);

  const renderHeaders = (headers: string[]) => (
    <TableRow>
      {headers.map((header, i) => (
        <TableCell
          key={header}
          scope="col"
          border={cellBorder}
          size={!isMobile && i > 0 ? `${currencyCellSizePct}%` : `${nameCellSizePct}%`}
          pad={{
            bottom: verticalPad,
            left: !i ? horizontalPad : undefined,
            right: i === tableHeaders.length - 1 ? horizontalPad : horizontalMargin,
          }}
          verticalAlign="top"
        >
          <Box direction="row" justify={i > 0 ? 'end' : 'start'}>
            <Text
              size={headerSize}
              lineHeight={lineHeight}
              weight={700}
              textAlign="end"
              style={{ maxWidth: maxHeaderWidth }}
            >
              {header}
            </Text>
          </Box>
        </TableCell>
      ))}
    </TableRow>
  );

  const isCanada = countryId === CountryId.Canada;

  const renderDataLine = (
    data: {
      programData?: IncentiveStatementProgram;
      facilityData?: IncentiveStatementFacility;
      categoryData?: IncentiveStatementEquipmentCategory;
      totalsData?: IncentiveStatementFinancials;
    },
    rowIndex?: number
  ) => {
    const { programData, facilityData, categoryData, totalsData } = data;
    if (!facilityData && !totalsData && !programData && !categoryData) return;

    const textSize = programData
      ? programValueSize
      : facilityData
        ? facilityValueSize
        : categoryData
          ? categoryValueSize
          : totalsSize;
    const border = totalsData ? totalsCellBorder : cellBorder;
    const textWeight = (programData && !isSummary) || totalsData ? 700 : undefined;

    const values: Record<string, string> = {};

    values.title = programData
      ? programData.name
      : facilityData
        ? facilityData.address
        : categoryData
          ? categoryData.name
          : t('statements:totals');

    const credits =
      programData?.credits ?? facilityData?.credits ?? categoryData?.credits ?? totalsData?.credits ?? '0';
    values.credits = !Number.isNaN(parseFloat(credits)) ? formatNumber(credits, true, false, false, 2) : '-';

    const credit_price = programData?.credit_price ?? facilityData?.credit_price ?? categoryData?.credit_price ?? '';
    values.credit_price = !Number.isNaN(parseFloat(credit_price))
      ? formatNumber(credit_price, true, false, false, 2)
      : '-';

    const total_value =
      programData?.total_value ??
      facilityData?.total_value ??
      categoryData?.total_value ??
      totalsData?.total_value ??
      '0';
    values.total_value = !Number.isNaN(parseFloat(total_value))
      ? formatNumber(total_value, true, false, false, 2)
      : '-';

    const rec_cost =
      programData?.rec_cost ?? facilityData?.rec_cost ?? categoryData?.rec_cost ?? totalsData?.rec_cost ?? '0';
    values.rec_cost = !Number.isNaN(parseFloat(rec_cost)) ? `(${formatNumber(rec_cost, true, false, false, 2)})` : '-';

    const taxes_on_credit_sale =
      programData?.taxes_on_credit_sale ??
      facilityData?.taxes_on_credit_sale ??
      categoryData?.taxes_on_credit_sale ??
      totalsData?.taxes_on_credit_sale ??
      '0';
    values.taxes_on_credit_sale = !Number.isNaN(parseFloat(taxes_on_credit_sale))
      ? formatNumber(taxes_on_credit_sale, true, false, false, 2)
      : '-';

    const broker_cost =
      programData?.broker_cost ??
      facilityData?.broker_cost ??
      categoryData?.broker_cost ??
      totalsData?.broker_cost ??
      '0';
    values.broker_cost = !Number.isNaN(parseFloat(broker_cost))
      ? `(${formatNumber(broker_cost, true, false, false, 2)})`
      : '-';

    const taxes_on_services =
      programData?.taxes_on_services ??
      facilityData?.taxes_on_services ??
      categoryData?.taxes_on_services ??
      totalsData?.taxes_on_services ??
      '0';
    values.taxes_on_services = !Number.isNaN(parseFloat(taxes_on_services))
      ? `(${formatNumber(taxes_on_services, true, false, false, 2)})`
      : '-';

    const fuse_service_fee =
      programData?.fuse_service_fee ??
      facilityData?.fuse_service_fee ??
      categoryData?.fuse_service_fee ??
      totalsData?.fuse_service_fee ??
      '0';
    values.fuse_service_fee = !Number.isNaN(parseFloat(fuse_service_fee))
      ? `(${formatNumber(fuse_service_fee, true, false, false, 2)})`
      : '-';

    const total_kwh =
      programData?.total_kwh ?? facilityData?.total_kwh ?? categoryData?.total_kwh ?? totalsData?.total_kwh ?? 0;
    const total_kwh_str = String(total_kwh);
    values.total_kwh = !Number.isNaN(parseInt(total_kwh_str))
      ? formatNumber(total_kwh_str, true, false, false, 0) || '0'
      : '-';

    const net_incentive =
      programData?.net_incentive ??
      facilityData?.net_incentive ??
      categoryData?.net_incentive ??
      totalsData?.net_incentive ??
      '0';
    values.net_incentive = !Number.isNaN(parseFloat(net_incentive))
      ? formatNumber(net_incentive, true, false, false, 2)
      : '-';

    return (
      <TableRow key={rowIndex}>
        <TableCell
          scope="row"
          border={border}
          size={`${nameCellSizePct}%`}
          pad={{
            vertical: verticalPad,
            right: horizontalMargin,
            left: categoryData ? categoryIndent : facilityData ? facilityIndent : horizontalPad,
          }}
          background={rowIndex !== undefined ? getBackground(rowIndex) : undefined}
        >
          <Box>
            <Text
              size={textSize}
              lineHeight={lineHeight}
              weight={programData || totalsData ? 700 : undefined}
              color={categoryData ? 'dark-4' : undefined}
            >
              {values.title}
            </Text>
          </Box>
        </TableCell>
        <TableCell
          scope="row"
          border={border}
          size={`${currencyCellSizePct}%`}
          pad={{ vertical: verticalPad, right: horizontalMargin }}
          background={rowIndex !== undefined ? getBackground(rowIndex) : undefined}
        >
          <Box direction="row" justify="end">
            <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
              {values.total_kwh}
            </Text>
          </Box>
        </TableCell>
        <TableCell
          scope="row"
          border={border}
          size={`${currencyCellSizePct}%`}
          pad={{ vertical: verticalPad, right: horizontalMargin }}
          background={rowIndex !== undefined ? getBackground(rowIndex) : undefined}
        >
          <Box direction="row" justify="end">
            <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
              {values.credits}
            </Text>
          </Box>
        </TableCell>
        <TableCell
          scope="row"
          border={border}
          size={`${currencyCellSizePct}%`}
          align={totalsData ? 'center' : undefined}
          pad={{ vertical: verticalPad, right: horizontalMargin }}
          background={rowIndex !== undefined ? getBackground(rowIndex) : undefined}
        >
          {values.credit_price === '-' && (
            <Box direction="row" justify="end">
              <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
                N/A
              </Text>
            </Box>
          )}
          {values.credit_price !== '-' && (
            <Box direction="row" justify="between" gap={pxToPt('3px')}>
              <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
                $
              </Text>
              <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
                {values.credit_price}
              </Text>
            </Box>
          )}
        </TableCell>
        <TableCell
          scope="row"
          border={border}
          size={`${currencyCellSizePct}%`}
          pad={{ vertical: verticalPad, right: horizontalMargin }}
          background={rowIndex !== undefined ? getBackground(rowIndex) : undefined}
        >
          <Box direction="row" justify="between" gap={pxToPt('3px')}>
            <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
              $
            </Text>
            <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
              {values.total_value}
            </Text>
          </Box>
        </TableCell>
        <TableCell
          scope="row"
          border={border}
          size={`${currencyCellSizePct}%`}
          pad={{ vertical: verticalPad, right: horizontalMargin }}
          background={rowIndex !== undefined ? getBackground(rowIndex) : undefined}
        >
          <Box direction="row" justify="between" gap={pxToPt('3px')}>
            <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
              $
            </Text>
            <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
              {isCanada ? values.taxes_on_credit_sale : values.rec_cost}
            </Text>
          </Box>
        </TableCell>
        <TableCell
          scope="row"
          border={border}
          size={`${currencyCellSizePct}%`}
          pad={{ vertical: verticalPad, right: horizontalMargin }}
          background={rowIndex !== undefined ? getBackground(rowIndex) : undefined}
        >
          <Box direction="row" justify="between" gap={pxToPt('3px')}>
            <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
              $
            </Text>
            <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
              {isCanada ? values.taxes_on_services : values.broker_cost}
            </Text>
          </Box>
        </TableCell>
        <TableCell
          scope="row"
          border={border}
          size={`${currencyCellSizePct}%`}
          pad={{ vertical: verticalPad, right: horizontalMargin }}
          background={rowIndex !== undefined ? getBackground(rowIndex) : undefined}
        >
          <Box direction="row" justify="between" gap={pxToPt('3px')}>
            <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
              $
            </Text>
            <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
              {values.fuse_service_fee}
            </Text>
          </Box>
        </TableCell>
        <TableCell
          scope="row"
          border={border}
          size={`${currencyCellSizePct}%`}
          pad={{ vertical: verticalPad, right: horizontalPad }}
          background={rowIndex !== undefined ? getBackground(rowIndex) : undefined}
        >
          <Box direction="row" justify="between" gap={pxToPt('3px')}>
            <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
              $
            </Text>
            <Text size={textSize} lineHeight={lineHeight} weight={textWeight}>
              {values.net_incentive}
            </Text>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const renderGrandTotal = (totals: IncentiveStatementFinancials) => (
    <Table>
      <TableBody>
        <TableRow>
          <TableCell
            scope="row"
            size={`${100 - currencyCellSizePct}%`}
            pad={{ vertical: verticalPad, right: horizontalMargin }}
          >
            <Box align="end">
              <Text size={grandTotalSize} lineHeight={lineHeight} color="brand" weight={700}>
                {t('statements:grandTotal')}
              </Text>
            </Box>
          </TableCell>
          <TableCell scope="row" size={`${currencyCellSizePct}%`} pad={{ vertical: verticalPad, right: horizontalPad }}>
            <Box direction="row" justify="between" gap={pxToPt('3px')}>
              <Text size={grandTotalSize} lineHeight={lineHeight} color="brand" weight={700}>
                $
              </Text>
              <Text size={grandTotalSize} lineHeight={lineHeight} color="brand" weight={700}>
                {formatNumber(totals.net_incentive, true, false, false, 2)}
              </Text>
            </Box>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );

  return (
    <Box fill="horizontal" {...boxProps}>
      <Table>
        <TableHeader>{renderHeaders(isCanada ? canadianHeaders : tableHeaders)}</TableHeader>
        <TableBody>
          {(() => {
            let rowCounter = 0;
            return normalized.programs.map((program) => [
              renderDataLine({ programData: program }, rowCounter++),
              !isSummary &&
                program.facilities?.map((facility) => {
                  const facilityRow = renderDataLine({ facilityData: facility }, rowCounter++);
                  const categoryRows =
                    facility.equipment_categories?.map((category) =>
                      renderDataLine({ categoryData: category }, rowCounter++)
                    ) || [];
                  return [facilityRow, ...categoryRows];
                }),
            ]);
          })()}
          <TableRow>
            <TableCell pad={{ vertical: verticalPad }} height={programValueSize} />
          </TableRow>
          {renderDataLine({ totalsData: normalized.totals })}
        </TableBody>
      </Table>
      {renderGrandTotal(normalized.totals)}
    </Box>
  );
};

export type IncentiveStatementDataTableProps = BoxProps & {
  incentiveStatement: IncentiveStatementResponse;
  isSummary?: boolean;
  countryId?: TCountryId;
};
